import util from '@/util'
import moment from 'moment-timezone'
import shiftsHelper from '@/helpers/shifts'
import { AUTH_STATUSES, SHIFT_STATUS } from '@/store/modules/shifts/constants'
import AuthReasonMixin from '../../authorizations/mixins/AuthReasonMixin'
import { getShiftTimeHighlightMetadata } from '../utils/shift-time-highlights'

export default {
  mixins: [shiftsHelper, AuthReasonMixin],
  data() {
    return { AUTH_STATUSES }
  },
  computed: {
    minWorkDuration() {
      return this.$store.state.applicationState.timeConstants.min_work_duration
    },
    minWorkWithWaiverDuration() {
      return this.$store.state.applicationState.timeConstants.min_work_with_waiver_duration
    },
    longWorkDuration() {
      return this.$store.state.applicationState.timeConstants.long_work_duration
    },
    longWorkWithWaiverDuration() {
      return this.$store.state.applicationState.timeConstants.long_work_with_waiver_duration
    }
  },
  methods: {
    renderScheduleType(scheduleType) {
      return { fixed: 'Strict', self_schedule: 'Flexible', free: 'Free' }[scheduleType]
    },
    formatDate(date) {
      return moment(date).format(this.$store.state.applicationState.timeFormat)
    },
    isLunchWaiveAvailable(start, end) {
      if (!start || !end) return false
      const duration = util.countDuration(start, end, 'minutes')
      return duration >= this.minWorkDuration && duration < this.minWorkWithWaiverDuration
    },
    isSecondLunchWaiveAvailable(start, end, firstLunchStart, firstLunchEnd) {
      if (!start || !end || !firstLunchStart || !firstLunchEnd) return false
      const duration = util.countDuration(start, end, 'minutes')
      const lunchDuration = util.countDuration(firstLunchStart, firstLunchEnd, 'minutes')
      return duration - lunchDuration >= this.longWorkDuration && duration - lunchDuration < this.longWorkWithWaiverDuration
    },
    getHighlightColor(authorization) {
      return this.$store.state.applicationState.extendedShiftAuthTypes.find(item => item.key === authorization)
        ?.auth_type === 'disciplinary'
        ? 'var(--warning-color)'
        : 'var(--error-color)'
    },
    getTimeHighlightMetadata(row, authorizations = [], prefix, shift) {
      return getShiftTimeHighlightMetadata({
        row,
        prefix,
        authorizationKeys: authorizations,
        shift,
        authStatuses: this.AUTH_STATUSES,
        authRules: this.AUTH_RULES,
        resolveColor: this.getHighlightColor,
        discrepancyThreshold: this.$store.state.applicationState.timeConstants.signed_actual_time_discrepancy
      })
    },
    getHighlight(row, authorizations = [], key, shift) {
      const highlight = this.getTimeHighlightMetadata(row, authorizations, key, shift)
      if (!highlight.messages.length) return row.value ?? '-'

      return (
        <a-tooltip>
          <template slot="title">
            {highlight.messages.map((message, index) => <div key={index}>{message}</div>)}
          </template>
          <div style={{ backgroundColor: highlight.color ?? 'transparent' }}>{row.value}</div>
        </a-tooltip>
      )
    },
    getMealBreakData(start, end) {
      const timeRange = this.formatTimeRange(start, end)
      return { rawStart: start, rawEnd: end, start: timeRange?.start, end: timeRange?.end }
    },
    getMealBreakValue(shift, mealData, abilityToWaive) {
      const { rawStart, rawEnd } = mealData || {}
      if (abilityToWaive && shift.lunch_waived && !rawStart) return 'Waived'
      if (!rawStart || !rawEnd) return '-'
      return util.getFormattedDuration(moment(rawStart), moment(rawEnd))
    },
    getTimeColumnHours(shift, key, field) {
      const hours = Number(shift.hours?.[`${key}${field}`] ?? 0)
      return util.formatMinutesToHoursAndMinutesDuration(Number.isFinite(hours) ? Math.round(hours * 60) : 0)
    },
    getCollectedDate(shift, key, authorizations) {
      const dateKey = key === 'scheduled_' ? 'scheduled' : `${key}work`
      const getShiftProp = suffix => shift[`${dateKey}_${suffix}`]
      const getLunchProp = suffix => shift[`${key}${suffix}`]
      const workStart = getShiftProp('start')
      const workEnd = getShiftProp('end')
      const date = util.convertDateTimeToCurrentTimeZoneWithFormat(workStart, this.$store.state.applicationState.dateFormat)
      const workTime = this.formatTimeRange(workStart, workEnd)
      const lunch = this.getMealBreakData(getLunchProp('lunch_start'), getLunchProp('lunch_end'))
      const secondLunch = this.getMealBreakData(getLunchProp('second_lunch_start'), getLunchProp('second_lunch_end'))
      const waiveFirstAbility = this.isLunchWaiveAvailable(workStart, workEnd)
      const waiveSecondAbility = this.isSecondLunchWaiveAvailable(workStart, workEnd, getLunchProp('lunch_start'), getLunchProp('lunch_end'))
      const workHours = this.getTimeColumnHours(shift, key, 'work_hours')
      const payHours = this.getTimeColumnHours(shift, key, 'pay_hours')

      if (key === 'scheduled_' && shift.pay_code) return <div class="text-center">-</div>
      if (key === 'actual_') {
        const actualContent = () => {
          if ((shift.manually_created || shift.adjusted) && !workStart) return <div class="text-center"><b>Manual</b></div>
          if (shift.status === 'scheduled' && !workStart) return <div class="text-center"><b>Scheduled</b></div>
          if (['started', 'cancelled', 'missed', 'deleted'].includes(shift.status)) {
            return <div class="text-center"><b>{SHIFT_STATUS[shift.status]?.title}</b></div>
          }
          if (shift.pay_code) return <div class="text-center">-</div>
          return null
        }
        if (actualContent()) {
          const hasAuth = authorizations.includes('no_actual_time_authorization')
          return <div class="text-center" style={{ backgroundColor: hasAuth ? this.getHighlightColor('no_actual_time_authorization') : 'transparent' }}>{actualContent()}</div>
        }
      }
      if (key === '' && shift.status === 'scheduled' && !workStart) return <div class="text-center"><b>Scheduled</b></div>
      if (key === '' && ['started', 'cancelled', 'missed', 'deleted'].includes(shift.status)) return <div class="text-center"><b>{SHIFT_STATUS[shift.status]?.title}</b></div>

      const baseRows = shift?.pay_code && shift.pay_code.type.toLowerCase() === 'total'
        ? [{ label: 'Date', value: date, key: 'date' }]
        : [
            { label: 'Date', value: date, key: 'date' },
            { label: 'Start', value: workTime?.start, key: 'start' },
            { label: 'End', value: workTime?.end, key: 'end' },
            { label: 'Work Hours', value: workHours, key: 'work_hours' },
            { label: 'Pay Hours', value: payHours, key: 'pay_hours' }
          ]
      const mealRows = (mealData, abilityToWaive, labelPrefix) => {
        const { rawStart, start, end } = mealData || {}
        const startKey = labelPrefix === '1st' ? 'lunch_start' : 'second_lunch_start'
        const endKey = labelPrefix === '1st' ? 'lunch_end' : 'second_lunch_end'
        return [{
          label: `${labelPrefix} Meal`,
          value: this.getMealBreakValue(shift, mealData, abilityToWaive),
          tooltip: start && end ? `${start} - ${end}` : '',
          key: abilityToWaive && shift.lunch_waived && !rawStart ? `waived_${startKey}` : startKey,
          highlightKeys: [startKey, endKey]
        }]
      }
      const infoRows = [...baseRows, ...mealRows(lunch, waiveFirstAbility, '1st'), ...mealRows(secondLunch, waiveSecondAbility, '2nd')]
      return <div>{infoRows.map((row, index) => <div key={index} class="flex gap-1 justify-content-between"><div class="w-6 text-left"><b>{row.label ? `${row.label}:` : '   '}</b></div><div class="w-6 text-right">{this.getHighlight(row, authorizations, key, shift)}</div></div>)}</div>
    },
    getViolationTimeRows(shift, authorizations = []) {
      const scheduled = this.formatTimeRange(shift.scheduled_start, shift.scheduled_end)
      const tracked = this.formatTimeRange(shift.actual_work_start, shift.actual_work_end)
      const signed = this.formatTimeRange(shift.work_start, shift.work_end)
      const signedLunch = this.formatTimeRange(shift.lunch_start, shift.lunch_end, true)
      const signedSecondLunch = this.formatTimeRange(shift.second_lunch_start, shift.second_lunch_end, true)
      const rangeValue = range => range === '-' || range.start === '-' ? '-' : `${range.start} - ${range.end}`
      const trackedValue = shift.actual_work_start && !shift.actual_work_end ? `${tracked.start} - Missing` : `${tracked.start} - ${tracked.end}`
      return [
        { label: 'Date', value: util.convertDateTimeToCurrentTimeZoneWithFormat(shift.scheduled_start || shift.work_start, this.$store.state.applicationState.dateFormat), key: 'context' },
        { label: 'Scheduled', value: rangeValue(scheduled), key: 'range', prefix: 'scheduled_', highlightKeys: ['start', 'end'] },
        { label: 'Tracked', value: shift.actual_work_start ? trackedValue : '-', key: 'range', prefix: 'actual_', highlightKeys: ['start', 'end'] },
        { label: 'Signed', value: rangeValue(signed), key: 'range', prefix: '', highlightKeys: ['start', 'end'] },
        { label: '1st Meal', value: signedLunch.start === 'Waived' ? 'Waived' : rangeValue(signedLunch), key: 'lunch_start', prefix: '', highlightKeys: ['lunch_start', 'lunch_end'] },
        { label: '2nd Meal', value: signedSecondLunch.start === 'Waived' ? 'Waived' : rangeValue(signedSecondLunch), key: 'second_lunch_start', prefix: '', highlightKeys: ['second_lunch_start', 'second_lunch_end'] },
        { label: 'Work Hours', value: this.getTimeColumnHours(shift, '', 'work_hours'), key: 'work_hours', prefix: '', highlightKeys: ['work_hours'] },
        { label: 'Pay Hours', value: this.getTimeColumnHours(shift, '', 'pay_hours'), key: 'pay_hours', prefix: '', highlightKeys: [] }
      ].map(row => ({ ...row, highlight: this.getTimeHighlightMetadata(row, authorizations, row.prefix, shift) }))
    },
    renderViolationTimeWidget(shift, authorizations = []) {
      const renderRow = row => {
        const content = <div key={row.label} class={['violations-time-row', row.highlight.color && `is-${row.highlight.color.includes('warning') ? 'warning' : 'error'}`]}><span class="violations-time-label">{row.label}</span><span class="violations-time-value">{row.value}</span></div>

        if (!row.highlight.messages.length) return content

        return <a-tooltip key={row.label}><template slot="title">{row.highlight.messages.map((message, index) => <div key={index}>{message}</div>)}</template>{content}</a-tooltip>
      }

      return <div class="violations-time-widget">{this.getViolationTimeRows(shift, authorizations).map(renderRow)}</div>
    }
  }
}
